import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink, useSearchParams } from 'react-router';
import { Badge } from 'softcat:components/badge.tsx';
import { ConfirmationModal } from 'softcat:components/confirmation-modal.tsx';
import { DataTable } from 'softcat:components/data-table.tsx';
import { SearchParametersOrderBy } from 'softcat:components/search-parameters-order-by.tsx';
import { SearchParametersPagination } from 'softcat:components/search-parameters-pagination.tsx';
import { SearchParametersSearchField } from 'softcat:components/search-parameters-search-field.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { BadgeState } from 'softcat:config/badge-state.ts';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { LocationService } from 'softcat:services/location.ts';
import useSWR, { preload } from 'swr';

export const Locations = () => {
    const intl = useIntl();
    const { activeTerritory } = useTerritory();
    const [searchParameters] = useSearchParams();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState({ name: '' });
    const [onRemoveErrorMessage, setOnRemoveErrorMessage] = useState('');
    const limit = Number.parseInt(searchParameters.get('limit') || '10');
    const page = Number.parseInt(searchParameters.get('page') || '1');
    const query = searchParameters.get('query') || '';
    const orderBy = searchParameters.get('orderBy') || '';

    const { data, error, isLoading, mutate } = useSWR(
        { key: `${activeTerritory}-LocationServiceFindAll`, limit, orderBy, page: page - 1, query },
        LocationService.findAll,
    );

    const columns = useMemo(() => {
        return [
            { key: 'name', value: intl.formatMessage({ id: 'LOCATIONS_PAGE_COLUMN_NAME' }), visible: true },
            { key: 'description', value: intl.formatMessage({ id: 'LOCATIONS_PAGE_COLUMN_DESCRIPTION' }), visible: true },
            { key: 'active', value: intl.formatMessage({ id: 'LOCATIONS_PAGE_COLUMN_STATUS' }), visible: true },
            { key: 'published', value: intl.formatMessage({ id: 'LOCATIONS_PAGE_COLUMN_PUBLISHED' }), visible: true },
            { key: 'actions', value: intl.formatMessage({ id: 'LOCATIONS_PAGE_COLUMN_ACTIONS' }), visible: false },
        ];
    }, [intl]);

    const removeLocation = async () => {
        try {
            await LocationService.remove({ name: selectedLocation.name });

            await mutate();

            setShowConfirmDelete(false);
        } catch (error) {
            setOnRemoveErrorMessage(`${intl.formatMessage({ id: 'LOCATIONS_PAGE_CONFIRM_DELETE_ERROR' })}: ${(error as Error).message}`);
        }
    };

    const rows = useMemo(() => {
        return (
            data?.map((location, index) => {
                return {
                    actions: (
                        <div className={'flex gap-4 justify-self-end'}>
                            <NavLink
                                data-testid={`edit-location-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_EDIT' })}
                                data-tooltip-id={'app-tooltip'}
                                onMouseEnter={() => {
                                    return preload({ key: `${activeTerritory}-LocationServiceFindOne`, name: location.name }, LocationService.findOne);
                                }}
                                to={`edit/${location.name}`}
                                type="button"
                            >
                                <PencilIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </NavLink>
                            <button
                                data-testid={`delete-location-${index}`}
                                data-tooltip-content={intl.formatMessage({ id: 'TOOLTIP_DELETE' })}
                                data-tooltip-id={'app-tooltip'}
                                onClick={() => {
                                    setSelectedLocation({ name: location.name });
                                    setOnRemoveErrorMessage('');
                                    setShowConfirmDelete(true);
                                }}
                            >
                                <TrashIcon className="size-5 scale-75 text-gray-800 transition-all hover:scale-100 hover:text-indigo-600" />
                            </button>
                        </div>
                    ),
                    active: location.active ? (
                        <Badge status={BadgeState.VALID}>
                            <FormattedMessage id={'LOCATIONS_PAGE_LOCATION_ACTIVE'} />
                        </Badge>
                    ) : (
                        <Badge status={BadgeState.ERROR}>
                            <FormattedMessage id={'LOCATIONS_PAGE_LOCATION_INACTIVE'} />
                        </Badge>
                    ),
                    description: location.description,
                    name: location.name,
                    published: location.published ? (
                        <Badge status={BadgeState.VALID}>
                            <FormattedMessage id={'LOCATIONS_PAGE_LOCATION_PUBLISHED'} />
                        </Badge>
                    ) : (
                        <Badge status={BadgeState.ERROR}>
                            <FormattedMessage id={'LOCATIONS_PAGE_LOCATION_UNPUBLISHED'} />
                        </Badge>
                    ),
                };
            }) || []
        );
    }, [activeTerritory, data, intl]);

    return (
        <div className="space-y-6 px-4">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <FormattedMessage id={'LOCATIONS_PAGE_HEADING'} />
                    </h1>

                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'LOCATIONS_PAGE_DESCRIPTION'} />
                    </p>
                </div>

                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                    <NavLink
                        className="flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                        to={'create'}
                        type="button"
                    >
                        <PlusIcon className={'size-3'} />
                        <FormattedMessage id={'LOCATIONS_PAGE_ADD_BUTTON'} />
                    </NavLink>
                </div>
            </div>

            <div className={'flex items-center'}>
                <div className={'grow'}>
                    <SearchParametersSearchField placeholder={intl.formatMessage({ id: 'LOCATIONS_PAGE_SEARCH_PLACEHOLDER' })} />
                </div>

                <div className={'pl-4'}>
                    <SearchParametersOrderBy
                        options={[
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_NONE' }), value: '' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_NAME_DESC' }), value: 'name:desc' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_NAME_ASC' }), value: 'name:asc' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_DESCRIPTION_DESC' }), value: 'description:desc' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_DESCRIPTION_ASC' }), value: 'description:asc' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_STATUS_DESC' }), value: 'active:desc' },
                            { label: intl.formatMessage({ id: 'LOCATIONS_PAGE_ORDER_BY_STATUS_ASC' }), value: 'active:asc' },
                        ]}
                    />
                </div>
            </div>

            {error && (
                <p className="mt-2 text-sm text-gray-700">
                    <FormattedMessage id={'LOCATIONS_PAGE_ERROR_LOADING_DATA'} />
                </p>
            )}

            {isLoading && <Spinner />}

            {!error && !isLoading && (
                <div>
                    {rows.length > 0 ? (
                        <>
                            <div className={'overflow-auto'}>
                                <DataTable columns={columns} rows={rows} />
                            </div>

                            <div className={'border-t border-t-gray-200 pt-2'}>
                                <SearchParametersPagination total={rows.length} />
                            </div>
                        </>
                    ) : (
                        <p className="mt-2 text-sm text-gray-700">
                            <FormattedMessage id={'LOCATIONS_PAGE_NO_RESULTS'} />
                        </p>
                    )}
                </div>
            )}

            <ConfirmationModal
                confirmButtonText={intl.formatMessage({ id: 'BUTTON_DELETE' })}
                confirmErrorMessage={onRemoveErrorMessage}
                content={intl.formatMessage({ id: 'LOCATIONS_PAGE_CONFIRM_DELETE_DESCRIPTION' }, { location: selectedLocation.name })}
                handleClose={() => {
                    setShowConfirmDelete(false);
                }}
                handleConfirm={async () => {
                    await removeLocation();
                }}
                header={intl.formatMessage({ id: 'LOCATIONS_PAGE_CONFIRM_DELETE_TITLE' })}
                isVisible={showConfirmDelete}
            />
        </div>
    );
};
